import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import lodashRange from 'lodash/range';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  hasRoleAdmin,
} from 'common/userRoles';

import {
  pickPartnerListingFields,
} from 'server/models/Partner/fieldgroups';

import postToMlsOptionItems from 'client/helpers/postToMlsOptionItems';

import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';

import SrGridRow from 'client/components/SrGridRow/SrGridRow';
import SrText from 'client/components/SrText';
import SrCard, {
  SR_CARD_BULLET_COLOR_BLUE,
  SR_CARD_CONTENT_TYPE_FORM_GRID,
} from 'client/components/SrCard/SrCard';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrFooterSaveButton from 'client/components/SrFooterSaveButton';

import {
  createFormFieldsByIdGetter,
  createInitialValues,
  createFormValidationFn,
  hideFieldIf,
} from 'client/helpers/formikHelpers';

const sumroomApplicationFeeOptionItems = lodashRange(0, 21)
  .map((value) => ({
    label: `$${50 + (value * 5)}`,
    value: `${50 + (value * 5)}`,
  }));

const partnerMarketApplicationFeeCommisionOptionItems = lodashRange(0, 21)
  .map((value) => ({
    label: `Base ${value > 0 ? `+ $${value * 5}` : ''}`,
    value: `${value * 5}`,
  }));

export const getPartnerListingSettingsFormFields = ({
  values,
  buyersAgentCommissions,
  filteredPartnerUsers,
  partnerMarketConfigurations,
}) => {
  return [
    {
      id: 'postToMls',
      title: 'Post To MLS',
      type: 'select',
      properties: {
        transformToBoolean: true,
        choices: postToMlsOptionItems,
      },
    },
    {
      id: 'coListingAgentMlsNumber',
      title: 'Add Co-Listing Agent on the MLS',
      type: 'select',
      properties: {
        choices: [
          {
            label: 'None, Choose Agent',
            value: null,
          },
          ...filteredPartnerUsers.map((partnerUser) => ({
            label: `${partnerUser.user.name} - ${partnerUser.agentMlsNumber}`,
            value: partnerUser.agentMlsNumber,
          })),
        ],
      },
    },
    ...(buyersAgentCommissions || []).reduce((memo, buyersAgentCommission, index) => ([
      ...memo,
      hideFieldIf(!values?.postToMls, {
        id: `buyersAgentCommissions[${index}].buyersAgentCommission`,
        title: buyersAgentCommission.marketDisplayName,
        type: 'percentage',
        properties: {
          pattern: '[0-9]*',
          placeholder: '100',
          onlyAcceptNumbers: true,
        },
        validations: ['required'],
      }),
      {
        id: `buyersAgentCommissions[${index}].marketId`,
        type: 'hidden',
      },
    ]), []),
    {
      id: 'administrationFee',
      title: 'Administration Fee',
      subtitle: 'Shown on marketing pages for renters to see.',
      type: 'currency',
    },
    {
      id: 'residentBenefitsPackageAmount',
      title: 'Monthly Resident Benefits Package',
      subtitle: 'Shown on marketing pages for renters to see.',
      type: 'currency',
    },
    {
      id: 'sunroomApplicationFee',
      title: 'Sunroom\'s Base App Fee',
      type: 'select',
      properties: {
        choices: sumroomApplicationFeeOptionItems,
        transformToFloat: true,
      },
    },
    ...(partnerMarketConfigurations.reduce((memo, partnerMarketConfiguration, index) => ([
      ...memo,
      {
        id: `partnerMarketConfigurations[${index}].partnerApplicationFee`,
        title: partnerMarketConfiguration.marketDisplayName,
        type: 'select',
        properties: {
          choices: partnerMarketApplicationFeeCommisionOptionItems,
          transformToFloat: true,
        },
      },
      {
        id: `partnerMarketConfigurations[${index}].marketId`,
        type: 'hidden',
      },
    ]), [])),
  ];
};

const SrPartnerListingSettingsForm = ({
  onSubmit,
  buyersAgentCommissions,
  filteredPartnerUsers,
  currentUser,
  partner,
  partnerMarketConfigurations,
}) => {
  const defaultValues = pickPartnerListingFields(partner);
  const defaultFormFields = getPartnerListingSettingsFormFields({
    filteredPartnerUsers,
    buyersAgentCommissions,
    partnerMarketConfigurations,
  });
  const isAdmin = hasRoleAdmin(currentUser.roles);

  return (
    <Formik
      enableReinitialize
      onSubmit={(values) => {
        onSubmit(setNullEmptyValues(values));
      }}
      initialValues={createInitialValues({
        formFields: defaultFormFields,
        defaultValues: {
          ...defaultValues,
          buyersAgentCommissions,
          partnerMarketConfigurations,
        },
      })}
      validate={(values) => {
        const formFields = getPartnerListingSettingsFormFields({
          filteredPartnerUsers,
          buyersAgentCommissions,
          partnerMarketConfigurations,
          values,
        });
        return createFormValidationFn(formFields)(values);
      }}
      render={(formikBag) => {
        const formFields = getPartnerListingSettingsFormFields({
          filteredPartnerUsers,
          buyersAgentCommissions,
          partnerMarketConfigurations,
          values: formikBag.values,
        });
        const getFormFieldsById = createFormFieldsByIdGetter(formFields);

        const buyersAgentCommissionsFields = formFields
          .filter(({ id }) => id.includes('buyersAgentCommissions'));

        return (
          <>
            <Form>
              <SrCard
                color={SR_CARD_BULLET_COLOR_BLUE}
                contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                header='Co-Listing Agent'
              >
                <SrGridRow autofit autofitMinColumnWidth='405px'>
                  <SrFormikField
                    formField={getFormFieldsById('coListingAgentMlsNumber')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                </SrGridRow>
              </SrCard>
              {isAdmin && (
                <SrCard
                  color={SR_CARD_BULLET_COLOR_BLUE}
                  contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                  header='MLS (Admin Only)'
                >
                  <SrGridRow autofit autofitMinColumnWidth='405px'>
                    <SrFormikField
                      formField={getFormFieldsById('postToMls')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  </SrGridRow>
                  {formikBag.values.postToMls && (
                    <SrGridRow autofit autofitMinColumnWidth='405px'>
                      {buyersAgentCommissionsFields.map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          formField={formField}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      ))}
                    </SrGridRow>
                  )}
                </SrCard>
              )}
              <SrCard
                color={SR_CARD_BULLET_COLOR_BLUE}
                contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                header='Advertised Fees'
              >
                <SrGridRow autofit autofitMinColumnWidth='405px'>
                  <SrFormikField
                    formField={getFormFieldsById('administrationFee')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                </SrGridRow>
                {partner.residentBenefitsPackageEnabled && (
                  <SrGridRow autofit autofitMinColumnWidth='405px'>
                    <SrFormikField
                      formField={getFormFieldsById('residentBenefitsPackageAmount')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  </SrGridRow>
                )}
              </SrCard>
              {isAdmin && (
                <SrCard
                  color={SR_CARD_BULLET_COLOR_BLUE}
                  contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                  header='App Fees (Admin Only)'
                >
                  <SrGridRow autofit autofitMinColumnWidth='405px'>
                    <SrFormikField
                      formField={getFormFieldsById('sunroomApplicationFee')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  </SrGridRow>
                  <SrGridRow autofit autofitMinColumnWidth='405px'>
                    {partnerMarketConfigurations.map((partnerMarketConfiguration, index) => (
                      <SrFormikField
                        key={partnerMarketConfiguration.id}
                        formField={
                          getFormFieldsById(`partnerMarketConfigurations[${index}].partnerApplicationFee`)
                        }
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                    ))}
                  </SrGridRow>
                </SrCard>
              )}
              <SrStaticFooter>
                <SrText
                  display='block'
                  lineHeight='25px'
                  font='greycliff'
                  color='grey600'
                  fontSize='13px'
                  weight='500'
                >
                  Updates can take 24 hours to syndicate
                </SrText>
                <SrFooterSaveButton isSubmitting={false} />
              </SrStaticFooter>
            </Form>
          </>
        );
      }}
    />
  );
};

SrPartnerListingSettingsForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  filteredPartnerUsers: PropTypes.array.isRequired,
  buyersAgentCommissions: PropTypes.array.isRequired,
  currentUser: PropTypes.object.isRequired,
  partner: PropTypes.object.isRequired,
};

SrPartnerListingSettingsForm.displayName = 'SrPartnerListingSettingsForm';

export default SrPartnerListingSettingsForm;
